import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { guestXPHelper, authService, userService, referralService } from './services/api';
import { installGlobalErrorReporting } from './services/errorReporter';
import { PageTracker } from './hooks/usePageTracking';
import { installStudyHeartbeat } from './services/studyHeartbeat';
import { installSessionTimeTracker } from './services/sessionTimeTracker';
import { installSessionPersistence } from './services/sessionPersistence';
import guestActivityTracker from './services/guestActivityTracker';
import Navbar from './components/Navbar';
import BrandLogo from './components/BrandLogo';
import ErrorBoundary from './components/ErrorBoundary';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import StepUpModal from './components/StepUpModal';
import SelfPacedPaywall from './components/SelfPacedPaywall';
import {
  clearCurriculumPreferenceCache,
  hydrateCurriculumPreferences,
} from './hooks/useCurriculumVersion';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import LanguageSelectPage from './pages/LanguageSelectPage';
import AppLinkPage from './pages/AppLinkPage';
import { getPreferredPublicLanguage, googleLocaleForPublicLanguage } from './utils/publicLanguage';
import { resolvePostAuthRedirect } from './utils/postAuthRedirect';
import './App.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const HomePage = lazy(() => import('./pages/HomePage'));
const ClassLessonsPage = lazy(() => import('./pages/ClassLessonsPage'));
const ClassLessonPage = lazy(() => import('./pages/ClassLessonPage'));
const ExercisePage = lazy(() => import('./pages/ExercisePage'));
const QuizPage = lazy(() => import('./pages/QuizPage'));
const FlashcardsPage = lazy(() => import('./pages/FlashcardsPage'));
const ReadingPage = lazy(() => import('./pages/ReadingPage'));
const MemoryPage = lazy(() => import('./pages/MemoryPage'));
const WritingPracticePage = lazy(() => import('./pages/WritingPracticePage'));
const ReviewPage = lazy(() => import('./pages/ReviewPage'));
const PlacementCheckPage = lazy(() => import('./pages/PlacementCheckPage'));
const LevelTestPage = lazy(() => import('./pages/LevelTestPage'));
const ProficiencyExamPage = lazy(() => import('./pages/ProficiencyExamPage'));
const ConversationPage = lazy(() => import('./pages/ConversationPage'));
const ContextPracticePage = lazy(() => import('./pages/ContextPracticePage'));
const ProgressPage = lazy(() => import('./pages/ProgressPage'));
const QuizDetailPage = lazy(() => import('./pages/QuizDetailPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminSpeakingDemo = lazy(() => import('./pages/AdminSpeakingDemo'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const ComparisonPage = lazy(() => import('./pages/ComparisonPage'));
const CohortsPage = lazy(() => import('./pages/CohortsPage'));
const CommunityPage = lazy(() => import('./pages/CommunityPage'));
const CommunityPostPage = lazy(() => import('./pages/CommunityPostPage'));
const CohortDetailPage = lazy(() => import('./pages/CohortDetailPage'));
const CohortSemesterPage = lazy(() => import('./pages/CohortSemesterPage'));
const CohortWelcomePage = lazy(() => import('./pages/CohortWelcomePage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const PersonalSemesterPage = lazy(() => import('./pages/PersonalSemesterPage'));
const InstitutionDashboard = lazy(() => import('./pages/InstitutionDashboard'));
const CertificateVerifyPage = lazy(() => import('./pages/CertificateVerifyPage'));
const CurriculumV2SessionShellPage = lazy(() => import('./pages/curriculumV2/SessionShellPage'));
const AlphabetOnboardingPage = lazy(() => import('./pages/curriculumV2/AlphabetOnboardingPage'));
const EnglishPhonicsPage = lazy(() => import('./pages/curriculumV2/EnglishPhonicsPage'));
const LessonCatalogPage = lazy(() => import('./pages/curriculumV2/LessonCatalogPage'));

// Listens for auth changes that should remove the current user from the app.
function AuthSessionListener({ onSessionEnded }) {
  const navigate = useNavigate();

  useEffect(() => {
    const handleSuspended = () => {
      onSessionEnded();
      navigate('/login', { state: { suspended: true } });
    };

    const handleExpired = () => {
      onSessionEnded();
      navigate('/login', { state: { sessionExpired: true } });
    };

    window.addEventListener('accountSuspended', handleSuspended);
    window.addEventListener('sessionExpired', handleExpired);
    return () => {
      window.removeEventListener('accountSuspended', handleSuspended);
      window.removeEventListener('sessionExpired', handleExpired);
    };
  }, [navigate, onSessionEnded]);

  return null;
}

function LegacyQuizRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/quiz/${id}` : '/quiz'} replace />;
}

// Auth state changes can re-render the route tree before LoginPage/RegisterPage
// finishes its own navigate() call. Resolve the same pending destination here
// so that race can never send a cohort applicant to Home.
function AuthenticatedAuthRedirect({ needsLanguageSetup = false }) {
  const location = useLocation();
  const destination = resolvePostAuthRedirect(location.search);

  if (needsLanguageSetup) {
    const params = new URLSearchParams({ mode: 'google-setup' });
    if (destination) params.set('redirect', destination);
    return <Navigate to={`/select-language?${params.toString()}`} replace />;
  }

  return <Navigate to={destination || '/'} replace />;
}

function GuestSignupPrompt({ onClose, onGuestExit }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const guestXP = guestXPHelper.get();

  const handleSignUp = () => {
    onClose();
    if (onGuestExit) onGuestExit();
    navigate('/select-language?mode=register');
  };

  const handleLogin = () => {
    onClose();
    if (onGuestExit) onGuestExit();
    navigate('/login');
  };

  return (
    <div className="guest-prompt-overlay">
      <div className="guest-prompt-modal">
        <button className="guest-prompt-close" onClick={onClose} aria-label={t('common.close', "Close")}>&times;</button>
        <div className="guest-prompt-icon">🎓</div>
        <h2>{t('guestPrompt.title', "You're doing great!")}</h2>
        {guestXP > 0 && (
          <p className="guest-prompt-xp" dangerouslySetInnerHTML={{ __html: t('guestPrompt.xpEarned', { defaultValue: "You've earned <strong>{{xp}} XP</strong> so far!", xp: guestXP }) }} />
        )}
        <p className="guest-prompt-message" dangerouslySetInnerHTML={{ __html: t('guestPrompt.message', "Create a free account to <strong>save your score</strong> and keep your progress safe. Without an account, your XP will be lost when you leave.") }} />
        <ul className="guest-prompt-benefits">
          <li>{t('guestPrompt.benefit1', "Save your XP and learning progress permanently")}</li>
          <li>{t('guestPrompt.benefit2', "Track your scores across lessons and flashcards")}</li>
          <li>{t('guestPrompt.benefit3', "Pick up right where you left off")}</li>
          <li>{t('guestPrompt.benefit4', "Access your stats and achievements")}</li>
        </ul>
        <div className="guest-prompt-actions">
          <button className="btn btn-primary guest-prompt-signup" onClick={handleSignUp}>
            {t('guestPrompt.signUpFree', "Sign Up Free")}
          </button>
          <button className="btn btn-outline guest-prompt-login" onClick={handleLogin}>
            {t('guestPrompt.alreadyHaveAccount', "Already have an account? Login")}
          </button>
        </div>
        <button className="guest-prompt-dismiss" onClick={onClose}>
          {t('guestPrompt.continueAsGuest', "Continue as guest")}
        </button>
      </div>
    </div>
  );
}

// Public marketing pages that render their own full brand header in a nav.
// The floating home-logo mark would duplicate that brand, so it stays hidden
// on these routes (the in-page brand already links back to home).
const OWN_BRAND_ROUTES = ['/', '/features', '/compare', '/comparison', '/cohorts'];

function GlobalHomeLogo({ hidden }) {
  const { t } = useTranslation();
  const location = useLocation();

  if (hidden || OWN_BRAND_ROUTES.includes(location.pathname)) {
    return null;
  }

  return (
    <Link to="/" className="global-home-logo" aria-label={t('common.backToHome', "Back to home")}>
      <BrandLogo variant="mark" decorative />
    </Link>
  );
}

// Admin screens inside the learner app paint purple (index.css,
// `:root[data-surface="admin"]`); every learner route keeps the brand theme.
// Only staff ever see /admin, so a learner bounced off it never flashes purple.
function AdminSurfaceTheme({ isStaff }) {
  const location = useLocation();
  const isAdminSurface = isStaff && /^\/admin(\/|$)/.test(location.pathname);

  useEffect(() => {
    if (!isAdminSurface) return undefined;
    const root = document.documentElement;
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const learnerThemeColor = themeMeta?.getAttribute('content');
    root.setAttribute('data-surface', 'admin');
    themeMeta?.setAttribute('content', '#7c3aed');
    return () => {
      root.removeAttribute('data-surface');
      if (themeMeta && learnerThemeColor) themeMeta.setAttribute('content', learnerThemeColor);
    };
  }, [isAdminSurface]);

  return null;
}

function RouteLoadingFallback() {
  const { t } = useTranslation();
  return <div className="loading">{t('common.loading', 'Loading...')}</div>;
}

// One-time prompt on login (or after target-language change) when the learner's
// current target has v2 available and they haven't yet chosen a version. Persists
// the choice via PUT /users/:id/curriculum-preference; "decide later" sets a
// session-scoped dismissal so the modal doesn't keep nagging the same session.
// v2 is the default curriculum experience, so we no longer prompt the user to
// choose. This gate only keeps the local preferences/availability cache in sync
// (so the Navbar and HomePage can route synchronously). Learners can still
// switch back to v1 per language from Settings/Profile.
function CurriculumVersionGate({ isAuthenticated, isGuest }) {
  const syncPrefs = useCallback(async () => {
    if (!isAuthenticated || isGuest) return;
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    try {
      const res = await userService.getProfile(userId);
      hydrateCurriculumPreferences(res.data?.curriculumV2 || {});
    } catch (_) {
      // Silent — a failed sync just leaves the cache as-is (default v2).
    }
  }, [isAuthenticated, isGuest]);

  useEffect(() => { syncPrefs(); }, [syncPrefs]);

  useEffect(() => {
    const handler = () => syncPrefs();
    window.addEventListener('targetLanguageChanged', handler);
    return () => window.removeEventListener('targetLanguageChanged', handler);
  }, [syncPrefs]);

  return null;
}

function App() {
  const { i18n } = useTranslation();
  const [googleLocale, setGoogleLocale] = useState(() => googleLocaleForPublicLanguage(getPreferredPublicLanguage()));
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem('token')
  );
  const [isGuest, setIsGuest] = useState(
    localStorage.getItem('guestMode') === 'true'
  );
  const [emailVerified, setEmailVerified] = useState(
    localStorage.getItem('emailVerified') !== 'false'
  );
  // Held in state (not read inline from localStorage) so a role change picked up
  // from the server on load re-renders the nav/admin gate without a re-login.
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole'));
  const [showGuestPrompt, setShowGuestPrompt] = useState(false);
  const [, setLanguagePairRevision] = useState(0);

  // Login/register write the authenticated user before calling this setter.
  // Read the role in the same React update so the privileged Admin item does
  // not appear a render later than the stable learning navigation.
  const setAuthenticatedSession = useCallback((authenticated) => {
    setUserRole(authenticated ? (localStorage.getItem('userRole') || 'user') : null);
    setIsAuthenticated(authenticated);
  }, []);

  useEffect(() => {
    installGlobalErrorReporting();
    // Heartbeat: fires `study_heartbeat` while the learner is interacting
    // with the app so passive engagement (autoplay, replay, browsing) still
    // resets the XP decay timer. Awards 0 XP — see services/studyHeartbeat.js.
    const uninstall = installStudyHeartbeat();
    // Renews the sign-in when a long-idle tab wakes up, and keeps every open
    // tab agreeing on who is signed in.
    const uninstallSession = installSessionPersistence();
    return () => {
      uninstall();
      uninstallSession();
    };
  }, []);

  // LanguageSelectPage persists the pair outside React state. Re-render the
  // route gates immediately after setup (and when another tab changes it) so a
  // successful selection cannot remain stuck behind a stale redirect.
  useEffect(() => {
    const refreshLanguagePair = () => setLanguagePairRevision((value) => value + 1);
    window.addEventListener('targetLanguageChanged', refreshLanguagePair);
    window.addEventListener('storage', refreshLanguagePair);
    return () => {
      window.removeEventListener('targetLanguageChanged', refreshLanguagePair);
      window.removeEventListener('storage', refreshLanguagePair);
    };
  }, []);

  useEffect(() => {
    const syncGoogleLocale = () => {
      setGoogleLocale(googleLocaleForPublicLanguage(getPreferredPublicLanguage()));
    };
    window.addEventListener('publicLanguageChanged', syncGoogleLocale);
    window.addEventListener('storage', syncGoogleLocale);
    return () => {
      window.removeEventListener('publicLanguageChanged', syncGoogleLocale);
      window.removeEventListener('storage', syncGoogleLocale);
    };
  }, []);

  // RTL support for Arabic and Hebrew
  useEffect(() => {
    const rtlLanguages = ['ar', 'he'];
    const dir = rtlLanguages.includes(i18n.language) ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  // Keep the app sized to the *visible* browser viewport. On mobile browsers,
  // 100vh often still includes the area behind the software keyboard, which
  // can trap focused fields or fixed composers below the usable screen.
  //
  // The reveal pass runs once per focus and once when the keyboard opens, never
  // on every viewport event. Android Chrome fires visualViewport scroll/resize
  // while the learner types (the keyboard's suggestion strip changes height),
  // and re-scrolling on each of those yanked the field away on every keystroke
  // and undid any manual scroll back to it. It also only moves a field that is
  // actually out of view, by the smallest distance that shows it.
  useEffect(() => {
    const viewport = window.visualViewport;
    let revealTimer;
    let keyboardOpen = false;

    const isEditable = (element) => (
      element instanceof HTMLElement
      && (element.matches('input, textarea, select') || element.isContentEditable)
    );

    // Visible band in layout coordinates: below the scroll owner's top edge
    // (the navbar sits above it) and above the keyboard. Only the first line
    // of a tall field has to be on screen, since that is where the caret lands.
    const isOutOfView = (element) => {
      const rect = element.getBoundingClientRect();
      const viewTop = viewport?.offsetTop || 0;
      const viewBottom = viewTop + (viewport?.height || window.innerHeight);
      const scroller = element.closest('.app-content');
      const bandTop = Math.max(viewTop, scroller ? scroller.getBoundingClientRect().top : viewTop);
      return rect.top < bandTop || rect.top + Math.min(rect.height, 48) > viewBottom;
    };

    const revealFocusedField = (behavior = 'smooth') => {
      const active = document.activeElement;
      if (!isEditable(active) || !isOutOfView(active)) return;
      active.scrollIntoView({ behavior, block: 'nearest', inline: 'nearest' });
    };

    const syncViewport = () => {
      const visibleHeight = Math.max(1, Math.round(viewport?.height || window.innerHeight));
      const coveredHeight = Math.max(0, Math.round(window.innerHeight - visibleHeight - (viewport?.offsetTop || 0)));
      document.documentElement.style.setProperty('--app-viewport-height', `${visibleHeight}px`);
      document.documentElement.style.setProperty('--app-keyboard-inset', `${coveredHeight}px`);

      const nowOpen = coveredHeight > 80;
      if (nowOpen && !keyboardOpen) {
        window.clearTimeout(revealTimer);
        revealTimer = window.setTimeout(() => revealFocusedField('smooth'), 80);
      }
      keyboardOpen = nowOpen;
    };

    const handleFocusIn = (event) => {
      if (!isEditable(event.target)) return;
      window.clearTimeout(revealTimer);
      revealTimer = window.setTimeout(() => revealFocusedField('smooth'), 180);
    };

    syncViewport();
    document.addEventListener('focusin', handleFocusIn);
    window.addEventListener('resize', syncViewport);
    viewport?.addEventListener('resize', syncViewport);
    viewport?.addEventListener('scroll', syncViewport);

    return () => {
      window.clearTimeout(revealTimer);
      document.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('resize', syncViewport);
      viewport?.removeEventListener('resize', syncViewport);
      viewport?.removeEventListener('scroll', syncViewport);
      document.documentElement.style.removeProperty('--app-viewport-height');
      document.documentElement.style.removeProperty('--app-keyboard-inset');
    };
  }, []);

  // Start / stop guest activity tracker alongside guest mode
  useEffect(() => {
    if (isGuest) {
      guestActivityTracker.init();
    } else {
      guestActivityTracker.destroy();
    }
  }, [isGuest]);

  // Guest signup prompt — first after 5 min, then every 30 min
  useEffect(() => {
    if (!isGuest) {
      setShowGuestPrompt(false);
      return;
    }
    let repeatTimer = null;
    const firstTimer = setTimeout(() => {
      setShowGuestPrompt(true);
      repeatTimer = setInterval(() => {
        setShowGuestPrompt(true);
      }, 30 * 60 * 1000);
    }, 5 * 60 * 1000);
    return () => {
      clearTimeout(firstTimer);
      if (repeatTimer) clearInterval(repeatTimer);
    };
  }, [isGuest]);

  // Listen for email verification events
  useEffect(() => {
    const handler = () => {
      setEmailVerified(true);
      localStorage.setItem('emailVerified', 'true');
    };
    window.addEventListener('emailVerified', handler);
    return () => window.removeEventListener('emailVerified', handler);
  }, []);

  // Rebuild who is signed in from the token itself on every load.
  //
  // The cached identity fields can go missing while the token is still perfectly
  // good — a sign-in interrupted halfway, storage cleared by the browser, a
  // profile request that failed on a flaky connection. When that happened the
  // app kept running as "signed in" with nothing to put in the greeting, so the
  // learner saw a nameless account and assumed they had been logged out.
  //
  // Asking the server who the token belongs to settles it both ways: the
  // identity comes back, or the session is genuinely over and the response
  // interceptor routes to sign-in.
  useEffect(() => {
    if (!isAuthenticated || isGuest) return;
    if (!localStorage.getItem('token')) return;

    authService.session()
      .then((res) => {
        const user = res.data?.user;
        if (!user) return;
        localStorage.setItem('userId', user.id);
        localStorage.setItem('username', user.username || '');
        localStorage.setItem('userFullName', user.fullName || '');
        localStorage.setItem('userEmail', user.email || '');
        if (user.languageSetupComplete === false) {
          localStorage.setItem('needsLanguageSetup', 'true');
          // Guarded so a learner already sitting on the setup page is not
          // bounced into a reload loop by this check.
          if (!window.location.pathname.startsWith('/select-language')) {
            window.location.replace('/select-language?mode=google-setup');
          }
          return;
        }
        // Pick up server-side role changes (e.g. promoted to admin/marketing)
        // so access updates without requiring a log out / log back in.
        const role = user.role || 'user';
        if (role !== localStorage.getItem('userRole')) {
          localStorage.setItem('userRole', role);
          setUserRole(role);
        }
      })
      .catch(() => {});
  }, [isAuthenticated, isGuest]);

  // Track time spent on platform for authenticated users
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!localStorage.getItem('token')) return;
    const userId = localStorage.getItem('userId');
    if (!userId) return;

    return installSessionTimeTracker({
      userId,
      sendMinutes: (minutes) => {
        authService.trackActivity(userId, minutes).catch(() => {});
      },
    });
  }, [isAuthenticated]);

  // Campaign attribution: when a visitor lands with ?ref=<code>, store the code
  // (so it survives navigation until they sign up) and record the visit. Runs
  // once on mount. Failures are swallowed so tracking never blocks page load.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (!ref) return;
    const code = ref.trim().toLowerCase().slice(0, 64);
    if (!code) return;
    localStorage.setItem('referralCode', code);
    referralService.recordHit(code).catch(() => {});
  }, []);

  const handleLogout = () => {
    // Invalidate the refresh chain server-side before clearing local state.
    // Fire-and-forget — UI never blocks on this, but it stops a stolen refresh
    // token from being replayed after the user signs out.
    authService.logout();
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('userFullName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
    setUserRole(null);
    localStorage.removeItem('subscriptionTier');
    localStorage.removeItem('aiEntitlements');
    localStorage.removeItem('guestMode');
    localStorage.removeItem('emailVerified');
    localStorage.removeItem('nativeLanguage');
    localStorage.removeItem('targetLanguage');
    localStorage.removeItem('languagePairConfirmed');
    localStorage.removeItem('needsLanguageSetup');
    localStorage.removeItem('activeLearningProgramId');
    clearCurriculumPreferenceCache();
    setIsAuthenticated(false);
    setIsGuest(false);
    setEmailVerified(true);
    i18n.changeLanguage('en');
  };

  const handleSessionEnded = useCallback(() => {
    localStorage.removeItem('activeLearningProgramId');
    setIsAuthenticated(false);
    setIsGuest(false);
  }, []);

  const handleGuestExit = () => {
    localStorage.removeItem('guestMode');
    setIsGuest(false);
  };

  // Check if user can access the app (either authenticated or guest)
  const canAccessApp = isAuthenticated || isGuest;
  const needsLanguageSetup = localStorage.getItem('needsLanguageSetup') === 'true';
  const storedNativeLanguage = localStorage.getItem('nativeLanguage');
  const storedTargetLanguage = localStorage.getItem('targetLanguage');
  const languagesReady = !!storedNativeLanguage
    && !!storedTargetLanguage
    && storedNativeLanguage !== storedTargetLanguage;
  const languageSetupRoute = isGuest
    ? '/select-language?mode=guest'
    : '/select-language?mode=google-setup';
  // Pair-dependent routes must all pass through the same gate. This prevents a
  // direct URL or deep link from bypassing the home-page setup redirect.
  const withLanguagePair = (element) => (
    canAccessApp && (needsLanguageSetup || !languagesReady)
      ? <Navigate to={languageSetupRoute} replace />
      : element
  );
  const localDemoPreviewAllowed = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const pageSearchParams = new URLSearchParams(window.location.search);
  const certificateRenderId = pageSearchParams.get('certificateRender') === '1'
    ? pageSearchParams.get('certificateId') || ''
    : '';
  const isCertificateRender = !!certificateRenderId;
  const showAppNavbar = canAccessApp && !needsLanguageSetup && languagesReady && !isCertificateRender;

  return (
    <GoogleOAuthProvider key={googleLocale} clientId={GOOGLE_CLIENT_ID} locale={googleLocale}>
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <PageTracker />
      <AdminSurfaceTheme isStaff={isAuthenticated && ['admin', 'marketing'].includes(userRole)} />
      <AuthSessionListener onSessionEnded={handleSessionEnded} />
      <StepUpModal />
      {isAuthenticated && <SelfPacedPaywall />}
      <CurriculumVersionGate isAuthenticated={isAuthenticated} isGuest={isGuest} />
      <div className={`App${showAppNavbar ? ' has-app-navigation' : ''}`}>
        <GlobalHomeLogo hidden={showAppNavbar || isCertificateRender} />
        {showAppNavbar && (
          <Navbar
            onLogout={handleLogout}
            isGuest={isGuest}
            onGuestExit={handleGuestExit}
            userRole={userRole}
          />
        )}
        <div className="app-content">
        {isAuthenticated && !emailVerified && !isGuest && !isCertificateRender && (
          <EmailVerificationBanner />
        )}
        {showGuestPrompt && isGuest && !isCertificateRender && (
          <GuestSignupPrompt
            onClose={() => setShowGuestPrompt(false)}
            onGuestExit={handleGuestExit}
          />
        )}
        <ErrorBoundary>
        <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          {/* Auth Routes */}
          <Route
            path="/login"
            element={
              isAuthenticated ? <AuthenticatedAuthRedirect needsLanguageSetup={needsLanguageSetup} /> : (
                <LoginPage
                  setIsAuthenticated={setAuthenticatedSession}
                  setIsGuest={setIsGuest}
                  setEmailVerified={setEmailVerified}
                />
              )
            }
          />
          <Route
            path="/register"
            element={
              isAuthenticated ? <AuthenticatedAuthRedirect needsLanguageSetup={needsLanguageSetup} /> : (
                <RegisterPage
                  setIsAuthenticated={setAuthenticatedSession}
                  setIsGuest={setIsGuest}
                  setEmailVerified={setEmailVerified}
                />
              )
            }
          />

          {/* Language selection — before register, guest mode, or google-setup */}
          <Route
            path="/select-language"
            element={
              <LanguageSelectPage
                setIsGuest={setIsGuest}
                onLogout={handleLogout}
              />
            }
          />

          {/* Email verification — always public */}
          <Route path="/app-link" element={<AppLinkPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/compare" element={<ComparisonPage />} />
          <Route path="/comparison" element={<ComparisonPage />} />
          <Route path="/cohorts" element={<CohortsPage />} />
          <Route path="/cohorts/:cohortId" element={<CohortDetailPage />} />
          <Route path="/personal-semester" element={isAuthenticated ? <PersonalSemesterPage /> : <Navigate to="/login" />} />
          <Route path="/cohorts/:cohortId/semester" element={isAuthenticated ? <CohortSemesterPage /> : <Navigate to="/login" />} />
          {/* Day-one arrival screen. The semester page sends first-time
              learners here; the route stays open afterwards so it can be
              revisited from a link. */}
          <Route path="/cohorts/:cohortId/welcome" element={isAuthenticated ? <CohortWelcomePage /> : <Navigate to="/login" />} />
          {/* The old interest form is replaced by real cohort registration. */}
          <Route path="/join-semester" element={<Navigate to="/cohorts" replace />} />
          <Route path="/certificates/verify" element={<CertificateVerifyPage />} />
          <Route path="/certificates/verify/:certificateId" element={<CertificateVerifyPage />} />
          <Route
            path="/demo-preview"
            element={
              localDemoPreviewAllowed ? (
                <AdminSpeakingDemo demoBypass />
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          {/* Main App Routes - Accessible by authenticated users and guests */}
          <Route
            path="/"
            element={
              isCertificateRender ? <CertificateVerifyPage certificateIdOverride={certificateRenderId} /> :
              canAccessApp ? withLanguagePair(<HomePage isGuest={isGuest} />) : <LandingPage />
            }
          />
          <Route
            path="/class"
            element={
              withLanguagePair(canAccessApp ? <ClassLessonsPage isGuest={isGuest} /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/class/:classLessonId"
            element={
              withLanguagePair(canAccessApp ? <ClassLessonPage /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/exercise"
            element={
              withLanguagePair(canAccessApp ? <ExercisePage /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/quiz"
            element={
              withLanguagePair(canAccessApp ? <QuizPage isGuest={isGuest} /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/quiz/:quizId"
            element={
              withLanguagePair(canAccessApp ? <QuizDetailPage isGuest={isGuest} /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/lessons"
            element={
              <LegacyQuizRedirect />
            }
          />
          <Route
            path="/lessons/:id"
            element={
              <LegacyQuizRedirect />
            }
          />
          <Route
            path="/flashcards"
            element={
              withLanguagePair(canAccessApp ? <FlashcardsPage isGuest={isGuest} /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/reading"
            element={
              withLanguagePair(isAuthenticated ? <ReadingPage /> : (
                isGuest ? <Navigate to="/" state={{ showSignupPrompt: true }} /> : <Navigate to="/login" />
              ))
            }
          />
          <Route
            path="/memory"
            element={
              withLanguagePair(canAccessApp ? <MemoryPage /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/writing"
            element={
              withLanguagePair(canAccessApp ? <WritingPracticePage /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/review"
            element={
              withLanguagePair(isAuthenticated ? <ReviewPage /> : (
                isGuest ? <Navigate to="/" state={{ showSignupPrompt: true }} /> : <Navigate to="/login" />
              ))
            }
          />
          <Route
            path="/level-check"
            element={
              withLanguagePair(isAuthenticated ? <PlacementCheckPage /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/level-tests"
            element={
              withLanguagePair(isAuthenticated ? <LevelTestPage /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/proficiency-exam"
            element={
              withLanguagePair(isAuthenticated ? <ProficiencyExamPage /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/conversation"
            element={
              withLanguagePair(canAccessApp ? <ConversationPage /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/context"
            element={
              withLanguagePair(canAccessApp ? <ContextPracticePage /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/learning-personalization"
            element={
              withLanguagePair(canAccessApp ? <ContextPracticePage /> : <Navigate to="/login" />)
            }
          />

          {/* Authenticated-only Routes */}
          <Route
            path="/progress"
            element={
              withLanguagePair(isAuthenticated ? <ProgressPage /> : (
                isGuest ? <Navigate to="/" state={{ showSignupPrompt: true }} /> : <Navigate to="/login" />
              ))
            }
          />
          <Route
            path="/community"
            element={
              withLanguagePair(isAuthenticated ? <CommunityPage /> : <Navigate to="/login" />)
            }
          />
          <Route
            path="/community/posts/:postId"
            element={isAuthenticated ? <CommunityPostPage /> : <Navigate to="/login" />}
          />
          <Route
            path="/profile/:profileTab?"
            element={
              isAuthenticated ? <ProfilePage onLogout={handleLogout} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/billing"
            element={
              isAuthenticated ? <BillingPage /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/institution"
            element={
              isAuthenticated ? <InstitutionDashboard /> : <Navigate to="/login" />
            }
          />

          {/* Curriculum v2 pilot (feature-flag gated inside the component) */}
          <Route
            path="/learn/v2"
            element={
              withLanguagePair(isAuthenticated ? <CurriculumV2SessionShellPage /> : <Navigate to="/login" />)
            }
          />
          {/* Hangul onboarding for Korean v2 — blocks A1 until completed, then
              stays reachable any time as a refresher (?mode=refresher). */}
          <Route
            path="/learn/v2/hangul"
            element={
              withLanguagePair(isAuthenticated ? <AlphabetOnboardingPage /> : <Navigate to="/login" />)
            }
          />
          {/* Sounds & spelling onboarding for English-target v2 — same gate/
              refresher role as Hangul, for non-Korean target languages. */}
          <Route
            path="/learn/v2/phonics"
            element={
              withLanguagePair(isAuthenticated ? <EnglishPhonicsPage /> : <Navigate to="/login" />)
            }
          />
          {/* Lesson catalog — browse + pick a specific concept or lesson. */}
          <Route
            path="/learn/v2/catalog"
            element={
              withLanguagePair(isAuthenticated ? <LessonCatalogPage /> : <Navigate to="/login" />)
            }
          />
          {/* The per-level completion test is retired; old links open the
              placement and proficiency choices. */}
          <Route path="/learn/v2/completion-test" element={<Navigate to="/level-tests" replace />} />

          {/* Admin Route */}
          <Route
            path="/admin/:adminTab?"
            element={
              isAuthenticated && ['admin', 'marketing'].includes(userRole) ? (
                <AdminDashboard />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          {/* Fallback: any unknown/removed path (e.g. a stale email link) → home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
        </div>
      </div>
    </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
